import { Box, Button, Stack, TableCell, TableRow, styled, useTheme } from '@mui/material';
import { DeleteIcon, MoveIcon, ShoppingCartIcon } from '@synergy/theme-next';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import {
    PoolBanner,
    PoolButton,
    PoolFormattedMessage,
    PoolImage,
    PoolLink,
    PoolQuantityPicker,
    PoolRow,
    PoolTextButton,
} from '@synergy/components-next';
import { PoolMath } from '@synergy/utils';
import { ReactElement, useState } from 'react';
import { useParams } from 'react-router';

import { ArticlePrice } from './article-price';
import { Attribute } from '../../../../models/attribute';
import { DeliveryStatus } from '../../../../components/delivery-status/delivery-status';
import { ROUTES } from '../../../../utils/routes';
import { VerticalDivider } from '../../../../components/vertical-divider/vertical-divider';
import { WishListItem, WishListItemStatus } from '../../../../models/wish-list';
import { WishListMoveItemModal } from '../../modals/wish-list-move-item-modal';
import { WishListType } from '../../../../models/enums/wish-list-type';
import { addArticleToCart } from '../../../../store/shopping-cart-article-actions';
import { getBannerMessage } from '../utlis/get-banner-message';
import { openInquiryModal } from '../../../../store/single-inquiry-actions';
import { removeFromWishlist, setWishListArticleQuantity } from '../../../../store/whish-list-actions';
import { useAppDispatch, useAppSelector } from '../../../../../store/store';
import { wishListDetailSelector, wishListsSelector } from '../../../../store/whish-list-selectors';

const ProductImage = styled(PoolImage)(() => ({
    display: 'block',
    objectFit: 'contain',
    height: 110,
    width: 110,
}));

export interface SupplierTableRowProps {
    cartItem: WishListItem;
    previewImage: string | undefined;
}

export const RowArticle = (props: SupplierTableRowProps): ReactElement => {
    const { cartItem, previewImage } = props;
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const wishList = useAppSelector(wishListDetailSelector);
    const [showMoveModal, setShowMoveModal] = useState(false);
    const { id: paramId = '' } = useParams<{ id?: string }>();
    const wishListId = +paramId || 'default';

    const article = cartItem.product;
    const moveArticleEnabled = useAppSelector(wishListsSelector).length > 1;

    const canEdit = wishListId === 'default' || wishList?.isOwner || wishList?.type === WishListType.OrganizationEdit;

    const [quantity, setQuantity] = useState(cartItem.quantity);

    const handleMoveArticle = (): void => {
        setShowMoveModal(true);
    };

    const handleCreateSingleInquiry = (): void => {
        dispatch(
            openInquiryModal({
                items: [{ id: article.id, articleName: article.name, quantity: article.minimumOrderQuantity }],
            })
        );
    };

    const handleDeleteArticle = (): void => {
        dispatch(removeFromWishlist(wishListId, cartItem.id));
    };

    const handleAddToShoppingCart = (): void => {
        dispatch(addArticleToCart(cartItem.product.id, quantity));
    };

    const handleQuantityChange = (quantity: number): void => {
        dispatch(setWishListArticleQuantity(wishListId, cartItem.id, quantity));
        setQuantity(quantity);
    };

    return (
        <>
            {cartItem.status !== WishListItemStatus.Available && (
                <TableRow>
                    <TableCell colSpan={4} sx={{ px: 0, pb: 0, pt: 2, borderBottom: 'none' }}>
                        <PoolBanner severity={cartItem.status === WishListItemStatus.Removed ? 'error' : 'warning'}>
                            <FormattedMessage id={getBannerMessage(cartItem.status)} />
                        </PoolBanner>
                    </TableCell>
                </TableRow>
            )}

            <TableRow>
                <TableCell>
                    <PoolRow alignItems="start" color="grey.800" gap={3}>
                        <Stack justifyContent="center">
                            <PoolLink href={ROUTES.article.detail.replace(':id', article.id.toString())}>
                                <ProductImage
                                    src={previewImage}
                                    alt={article.name}
                                    sx={{
                                        opacity: cartItem.status === WishListItemStatus.Removed ? 0.5 : 1,
                                    }}
                                />
                            </PoolLink>
                        </Stack>

                        <Stack gap={1.5} typography="body2">
                            <Stack gap={0.5} sx={{ opacity: cartItem.status === WishListItemStatus.Removed ? 0.5 : 1 }}>
                                <PoolLink
                                    colorVariant="black"
                                    href={ROUTES.article.detail.replace(':id', article.id.toString())}
                                    typography="subtitle1"
                                >
                                    {article?.name}
                                </PoolLink>
                                <PoolRow divider={<VerticalDivider />} gap={1} flexWrap="wrap">
                                    <PoolFormattedMessage
                                        id={'app.cart.article.articleNumber'}
                                        values={{ articleNumber: article.number }}
                                    />
                                    {article.globalTradeItemNumber && (
                                        <PoolFormattedMessage
                                            id={'app.cart.article.globalNumber'}
                                            values={{ articleNumber: article.globalTradeItemNumber }}
                                        />
                                    )}
                                </PoolRow>
                                {article.attributes.length !== 0 && (
                                    <PoolRow divider={<VerticalDivider />} gap={1} flexWrap="wrap">
                                        {article?.attributes.map((attribute: Attribute) => {
                                            return (
                                                <Box key={attribute.value}>
                                                    {attribute.name} {attribute.value}
                                                </Box>
                                            );
                                        })}
                                    </PoolRow>
                                )}
                            </Stack>

                            <PoolRow sx={{ my: 1.25 }} flexWrap="wrap" gap={3} rowGap={1}>
                                <DeliveryStatus
                                    variant="circle"
                                    deliveryStatus={article.deliveryStatus}
                                    stockStatus={article.stockStatus}
                                    deliveryTimeInDays={article.deliveryTimeInDays}
                                />

                                {cartItem.status === WishListItemStatus.Available && canEdit && (
                                    <PoolTextButton
                                        startIcon={<MoveIcon />}
                                        onClick={handleMoveArticle}
                                        disabled={!moveArticleEnabled}
                                    >
                                        <PoolFormattedMessage id={'app.wishList.move'} />
                                    </PoolTextButton>
                                )}

                                {canEdit && (
                                    <PoolTextButton
                                        startIcon={<DeleteIcon />}
                                        onClick={handleDeleteArticle}
                                        color={cartItem.status === WishListItemStatus.Removed ? 'error' : 'grey.700'}
                                        hoverColor={cartItem.status === WishListItemStatus.Removed ? 'black' : 'error'}
                                    >
                                        <PoolFormattedMessage id={'app.cart.article.delete'} />
                                    </PoolTextButton>
                                )}
                            </PoolRow>
                        </Stack>
                    </PoolRow>
                </TableCell>

                <TableCell align="right" sx={{ verticalAlign: 'top', width: 129 }}>
                    {cartItem.status === WishListItemStatus.Available && (
                        <ArticlePrice article={article} netPrice={cartItem.netPrice} />
                    )}
                </TableCell>

                <TableCell align="right" sx={{ verticalAlign: 'top', width: 120 }}>
                    {cartItem.status === WishListItemStatus.Available && (
                        <Box
                            color={article.specialPrice?.price ? theme.palette.error.dark : theme.palette.common.black}
                            typography="body1"
                            alignSelf="flex-start"
                            fontWeight={600}
                            width={120}
                            marginLeft="auto"
                        >
                            <FormattedNumber
                                value={PoolMath.round(cartItem.netPrice * quantity)}
                                currency={article.currency}
                                style="currency"
                            />
                        </Box>
                    )}
                </TableCell>

                <TableCell align="right" sx={{ verticalAlign: 'top', width: 274 }}>
                    {cartItem.status === WishListItemStatus.Available ? (
                        <Stack typography="body2" color="grey.800" alignItems="flex-end" gap={1}>
                            <PoolButton startIcon={<ShoppingCartIcon />} onClick={handleAddToShoppingCart}>
                                <FormattedMessage id="app.article.shoppingCart.add" />
                            </PoolButton>
                            <PoolQuantityPicker
                                quantity={cartItem.quantity}
                                minQuantity={article.minimumOrderQuantity}
                                maxQuantity={article.maximumOrderQuantity}
                                interval={article.quantityInterval}
                                onQuantityChange={handleQuantityChange}
                                disabled={!canEdit}
                                sx={{ '& .MuiOutlinedInput-root': { width: 170 }, mt: 1 }}
                            />
                            <Box sx={{ whiteSpace: 'pre' }}>
                                <Box>
                                    <FormattedMessage
                                        id="app.cart.article.quantity"
                                        values={{
                                            orderUnit: article.orderUnit,
                                            contentQuantity: article.contentQuantity,
                                            contentUnit: article.contentUnit,
                                            optional: chunks =>
                                                article.contentQuantity && article.contentUnit && chunks,
                                        }}
                                    />
                                </Box>
                                {(article.minimumOrderQuantity ||
                                    (article.quantityInterval && article.quantityInterval !== 1)) && (
                                    <Stack direction="row">
                                        {'('}
                                        <Stack
                                            direction="row"
                                            typography="body2"
                                            color="grey.800"
                                            gap={1}
                                            divider={<VerticalDivider />}
                                        >
                                            {article.minimumOrderQuantity && (
                                                <Box>
                                                    <PoolFormattedMessage
                                                        id={'app.cart.article.minimumQuantity'}
                                                        values={{ minimumQuantity: article.minimumOrderQuantity }}
                                                    />
                                                </Box>
                                            )}
                                            {article.quantityInterval && article.quantityInterval !== 1 && (
                                                <PoolFormattedMessage
                                                    id={'app.cart.article.interval'}
                                                    values={{
                                                        interval: article.quantityInterval,
                                                    }}
                                                />
                                            )}
                                        </Stack>
                                        {')'}
                                    </Stack>
                                )}
                            </Box>
                        </Stack>
                    ) : (
                        <Button variant="outlined" onClick={handleCreateSingleInquiry}>
                            <FormattedMessage id="app.singleInquiry" />
                        </Button>
                    )}
                </TableCell>
            </TableRow>
            <WishListMoveItemModal
                open={showMoveModal}
                wishListItem={cartItem}
                onClose={() => setShowMoveModal(false)}
            />
        </>
    );
};
